import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AI_AGENT_MANUAL_UPLOAD_CHANNELS,
  AI_AGENT_TASK_STATUSES,
  manualUploadChannelOptions,
  taskStatusOptions,
} from '../../../../../../../shared/constants/aiAgentOptions';
import { cleanParams } from '../../../../../../../shared/utils/cleanParams';
import { useModelOptions } from '../../../Common/hooks/useModelOptions';

import type { KeyboardEvent, MouseEvent } from 'react';
import type { IAsCsFilesParams } from '../../../../../../../shared/apis/ai-agent/ascs/type';
import type { AiAgentAsCsFileSearchType, IAiAgentAsCsFileFilterForm } from '../types';

const initialFilterForm: IAiAgentAsCsFileFilterForm = {
  serviceChannels: [],
  taskStatuses: [],
  modelId: null,
};

const initialSearchType: AiAgentAsCsFileSearchType = 'FILE_NAME';

const isAiAgentAsCsFileSearchType = (value: string): value is AiAgentAsCsFileSearchType => {
  return value === 'FILE_NAME' || value === 'TEMPLATE_NAME';
};

const isAiAgentTaskStatus = (value: string): value is IAiAgentAsCsFileFilterForm['taskStatuses'][number] => {
  return value !== AI_AGENT_TASK_STATUSES.ALL && taskStatusOptions.some((option) => option.value === value);
};

const parseFilterValues = <T extends string>(value: string | null, isValidValue: (item: string) => item is T): T[] => {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter(isValidValue),
    ),
  );
};

const isAiAgentAsCsFileServiceChannel = (
  value: string,
): value is IAiAgentAsCsFileFilterForm['serviceChannels'][number] => {
  return value !== AI_AGENT_MANUAL_UPLOAD_CHANNELS.ALL && manualUploadChannelOptions.some((option) => option.value === value);
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export default function useAiAgentAsCsFileFilters(companyId: number) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IAiAgentAsCsFileFilterForm>(initialFilterForm);
  const [searchType, setSearchType] = useState<AiAgentAsCsFileSearchType>(initialSearchType);

  const serviceChannelParam = searchParams.get('serviceChannel');
  const taskStatusParam = searchParams.get('taskStatus');
  const modelIdParam = searchParams.get('modelId') || '';
  const searchTypeParam = searchParams.get('searchType') || initialSearchType;
  const keywordParam = searchParams.get('keyword') || '';

  const modelOptions = useModelOptions(companyId);
  const serviceChannels = parseFilterValues(serviceChannelParam, isAiAgentAsCsFileServiceChannel);
  const taskStatuses = parseFilterValues(taskStatusParam, isAiAgentTaskStatus);
  const parsedModelId = parsePositiveInteger(modelIdParam, 0);
  const appliedSearchType = isAiAgentAsCsFileSearchType(searchTypeParam) ? searchTypeParam : initialSearchType;

  const queryParams: IAsCsFilesParams = {
    companyId,
    page: parsePositiveInteger(searchParams.get('page'), 1),
    size: parsePositiveInteger(searchParams.get('size'), 10),
    modelId: parsedModelId || undefined,
    taskStatuses: taskStatuses.join(',') || undefined,
    regServiceChannels: serviceChannels.join(',') || undefined,
    searchType: appliedSearchType,
    keyword: keywordParam.trim() || undefined,
  };

  const updateFilterForm = (nextFilter: Partial<IAiAgentAsCsFileFilterForm>) => {
    setFilterForm((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const handleSearchType = (value: string | null) => {
    setSearchType(value && isAiAgentAsCsFileSearchType(value) ? value : initialSearchType);
  };

  const resetSearchInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const applyFilters = () => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        uploadStartDt: '',
        uploadEndDt: '',
        serviceChannel: filterForm.serviceChannels.join(','),
        taskStatus: filterForm.taskStatuses.join(','),
        modelId: filterForm.modelId || '',
        searchType,
        keyword: inputRef.current?.value.trim() || '',
      }),
    );
  };

  const handleSearch = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    if ('key' in event && event.key !== 'Enter') {
      return;
    }

    applyFilters();
  };

  const handlePageChange = (page: number) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: String(page),
      }),
    );
  };

  const resetFilters = () => {
    resetSearchInput();
    setSearchType(initialSearchType);
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        uploadStartDt: '',
        uploadEndDt: '',
        serviceChannel: '',
        taskStatus: '',
        modelId: '',
        searchType: initialSearchType,
        keyword: '',
      }),
    );
  };

  useEffect(() => {
    setFilterForm({
      serviceChannels: parseFilterValues(serviceChannelParam, isAiAgentAsCsFileServiceChannel),
      taskStatuses: parseFilterValues(taskStatusParam, isAiAgentTaskStatus),
      modelId: modelIdParam || null,
    });
    setSearchType(isAiAgentAsCsFileSearchType(searchTypeParam) ? searchTypeParam : initialSearchType);

    if (inputRef.current) {
      inputRef.current.value = keywordParam;
    }
  }, [keywordParam, modelIdParam, searchTypeParam, serviceChannelParam, taskStatusParam]);

  return {
    filterForm,
    queryParams,
    searchType,
    inputRef,
    modelOptions,
    updateFilterForm,
    handlePageChange,
    handleSearchType,
    handleSearch,
    resetSearchInput,
    resetFilters,
  };
}
